<script lang="ts" setup>
import '@unocss/reset/tailwind.css'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'


import { runRetentionAction, useRetentionFloatingState } from '@/composables/useRetentionSubscriptionPopup'
import BizRetentionFloatingButton from '@/components/BizRetentionFloatingButton.vue'
import BizRetentionSubscriptionPopup from '@/components/BizRetentionSubscriptionPopup.vue'
import BizTopAppBanner from '@/components/BizTopAppBanner.vue'
import { useTopAppBanner } from '@/composables/useTopAppBanner'

import AudioPlayerPopup from '@/components/AudioPlayerPopup.vue'
import AudioPlayerMini from '@/components/AudioPlayerMini.vue'
import { useAudioPlayerStore, useToastStore, useUserStore } from '@/stores'
import { usePageTime, useTechAppHide, useTechHttpCost } from '@/composables/tracks'
import { resetExternalPaymentRedirectPending } from '@/composables/payment/composables/paymentUtils'
import { useSubscription } from '@/composables/payment'
import { brand, features, theme } from '@/config/brands'
import { loadBrandComponent } from '@/utils/loadBrandConfig'
import { $getToken } from '@/helpers'
import { createDebugger } from '@/utils/debug'
import { VIP_TYPE } from '@/constants'
import { getRetentionCheckoutCancelAction } from '@/utils/retentionCheckoutStorage'
import type { RetentionCheckoutCancelAction } from '@/utils/retentionCheckoutStorage'

const BrandExperience = loadBrandComponent({ rocnovel: null })

const { visible: topBannerVisible, close: closeTopBanner } = useTopAppBanner()
const route = useRoute()
const router = useRouter()
const retentionDebug = createDebugger('retention-popup')
const isFlipnovelFamily = ['flipnovel', 'storlyn', 'moonpage', 'novaris', 'novelune'].includes(brand)
// NovelMello 是独立品牌，不属于 flipnovel 家族：
// 不用家族的全局头部/初始 Loading，只共享"#app 是滚动容器"这一全局事实
const isNovelmello = brand === 'novelmello'
// #app 上的皮肤 class：NovelMello 只挂 brand-novelmello 单 class
const brandSkinClass = (isFlipnovelFamily || isNovelmello || brand === 'novelsprig' || brand === 'superstory') ? `brand-${brand}` : ''
const isGoldLoadingBrand = brand === 'aurenovel'
const enableRetentionSubscriptionPopup = features?.enableRetentionSubscriptionPopup === true
const aurenovelPreviewGold = '#FFB300'

// 修复 flipnovel 家族站点：body/html 被 overflow:hidden，实际滚动在 #app 上，
// Vue Router scrollBehavior 只滚 window，导致各页面共享滚动偏移。
// 切页时手动重置 #app 的 scrollTop，其他品牌不受影响（#app 不会滚动）。
watch(() => route.path, () => {
  if (!isFlipnovelFamily && !isNovelmello)
    return

  const app = document.getElementById('app')
  if (app)
    app.scrollTop = 0
})

const loading = ref(true)
// 外部页面离开当前文档后保持为 true；若阅读器从 BFCache 恢复，路由可能
// 先收到 payment_status=cancel，随后 pageshow 才触发阅读器的强制 reload。
// 用该标记避免在 reload 前提前消费挽留支付 pending。
let pageHiddenForExternalNavigation = false
// 路由初始导航解析完成前，route 是 START 占位对象；该标记防止支付返回恢复
// 逻辑在路由未就绪时误判路径、提前消费挽留 pending。
let retentionRouteReady = false
const { performanceObserver } = useTechHttpCost()
const { primaryColor, bgGlobal } = theme

function hexToRgba(color: string, alpha: number) {
  const normalized = color.replace('#', '')
  if (normalized.length !== 6)
    return `rgba(255, 138, 76, ${alpha})`

  const r = Number.parseInt(normalized.slice(0, 2), 16)
  const g = Number.parseInt(normalized.slice(2, 4), 16)
  const b = Number.parseInt(normalized.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// 只有 flipnovel 家族品牌才需要自定义 Loading 和 Header
const AppInitialLoading = isFlipnovelFamily
  ? loadBrandComponent({ rocnovel: null })
  : null
const AppGlobalHeader = isFlipnovelFamily
  ? loadBrandComponent({ rocnovel: null })
  : null

function initApp() {
  performanceObserver()
  useTechAppHide()

  // 设置全局品牌主题 CSS 变量
  document.documentElement.style.setProperty('--primary-color', primaryColor)

  // 设置按钮背景（支持渐变或单色回退）
  const btnBackground = theme.primaryGradient || primaryColor
  document.documentElement.style.setProperty('--btn-primary-bg', btnBackground)

  // 需要 CSS ring loading 的品牌设置 spinner 相关 CSS 变量
  if (isFlipnovelFamily || isGoldLoadingBrand || isNovelmello) {
    const loadingColor = isGoldLoadingBrand ? aurenovelPreviewGold : primaryColor
    document.documentElement.style.setProperty('--loading-spinner-color', loadingColor)
    document.documentElement.style.setProperty('--loading-spinner-border-color', hexToRgba(loadingColor, 0.26))
  }

  if (isGoldLoadingBrand)
    document.documentElement.style.setProperty('--loading-panel-bg', 'rgba(255, 249, 232, 0.96)')
}

onMounted(async () => {
  // eslint-disable-next-line no-console
  console.log('[App] => onMounted')
  loading.value = false

  usePageTime()

  // 外部支付页会冻结当前 H5 页面；主动清理提示，避免从 BFCache 返回时展示过期错误。
  window.addEventListener('pagehide', () => {
    pageHiddenForExternalNavigation = true
    useToastStore().$clearToast()
  })
  // 从外部支付页通过浏览器返回时，页面可能由 BFCache 恢复；复位跳转期间的请求错误抑制。
  window.addEventListener('pageshow', (event) => {
    resetExternalPaymentRedirectPending()
    // 两种阅读器会在 BFCache 恢复后主动 reload 以刷新章节状态。此处先保留
    // pending 标记，让 reload 后的新 App 实例恢复弹窗，避免恢复后随即又丢失。
    if (event.persisted && (route.name === 'chapter' || route.name === 'chapter-scroll'))
      return
    pageHiddenForExternalNavigation = false
    // PayPal 页面通过浏览器返回/手势退出时不会请求 cancelUrl，也就没有
    // payment_status=cancel；根据仍有效的挽留支付标记恢复原弹窗。
    tryRestoreGlobalRetentionAfterCheckout(true)
  })

  // 部分移动浏览器返回时不会使用 BFCache，而会完整重载当前页。
  // 此时 pageshow 可能早于异步页面初始化完成，挂载后先补做一次恢复检查。
  // 注意：必须在路由初始导航解析完成后再检查。onMounted 时 route 仍是 START
  // 占位（path='/'、无 query），会把 /paying 中转页误判为普通页面并提前消费
  // pending 标记，导致 1.5s 后 replace 到阅读器时已无法恢复弹窗。
  router.onReady(() => {
    retentionRouteReady = true
    tryRestoreGlobalRetentionAfterCheckout(true)
  })

  // 恢复音频播放器状态
  const audioStore = useAudioPlayerStore()
  const restored = audioStore.restoreState()
  const isPayingRoute = route.path === '/paying'
  // eslint-disable-next-line no-console
  console.log('[App] audio restoreState:', restored, 'payment_status=', route.query.payment_status, 'paymentRequiredChapterId=', audioStore.paymentRequiredChapterId, 'route=', route.path)
  if (restored && !isPayingRoute) {
    // token 不存在（如清除站点数据后 sessionStorage 残留旧状态），强制清理
    if (!$getToken()) {
      // eslint-disable-next-line no-console
      console.log('[App] no token found, closing audio player and clearing stale state')
      audioStore.closePlayer()
      return
    }
    audioStore.miniMode = true
    audioStore.visible = false
    const isPaymentReturn = route.query.payment_status === 'success'
    if (audioStore.paymentRequiredChapterId && (isPaymentReturn || !audioStore.chapterList.find(ch => ch.id === audioStore.paymentRequiredChapterId)?.ttsAudioUrl)) {
      // eslint-disable-next-line no-console
      console.log('[App] payment return detected, restoring popup and resuming play')
      audioStore.visible = true
      audioStore.miniMode = false
      await audioStore.resumePlayAfterPayment()
    }
    else if (isPaymentReturn) {
      // eslint-disable-next-line no-console
      console.log('[App] payment return but no pending chapter, just show mini-player')
    }
    // 清理 URL 参数
    {
      const url = new URL(window.location.href)
      if (isPaymentReturn)
        url.searchParams.delete('payment_status')
      if (url.searchParams.has('recommend') || url.searchParams.has('recommendChapterId')) {
        // eslint-disable-next-line no-console
        console.log('[App] cleaning up stale recommend params (set by audio payment flow)')
        url.searchParams.delete('recommend')
        url.searchParams.delete('recommendChapterId')
        window.history.replaceState({}, '', url.href)
      }
    }
  }
  else if (isPayingRoute) {
    // eslint-disable-next-line no-console
    console.log('[App] on /paying route, skip audio restore')
  }
  else {
    // eslint-disable-next-line no-console
    console.log('[App] no audio state to restore')
  }
})
initApp()

const {
  popupVisible: globalPopupVisible,
  floatingVisible,
  floatingHiddenWhileScrolling,
  countdown: globalCountdown,
  countdownText: globalCountdownText,
  showCloseButton: globalShowCloseButton,
  selectedProduct: globalRetentionProduct,
  showPopup,
  restorePopup,
  hidePopup,
  dismissPopup,
  shouldRestoreOnCheckoutCancel,
  clearPendingPaymentCheck,
} = useRetentionFloatingState()

// 支付成功兜底：只要用户数据确认已成为全站阅读 VIP，就立即作废整个挽留会话
// （清空倒计时、档位、pending 标记并隐藏弹窗）。覆盖支付成功但回跳链路未携带
// payment_status=success 的边缘情况（异步到账、验单延迟、URL 参数被清理等）。
const retentionUserStore = useUserStore()
watch(() => [
  retentionUserStore.$state.subscribe,
  retentionUserStore.$state.subscribeType,
  retentionUserStore.$state.vipEndTime,
] as const, () => {
  if (!enableRetentionSubscriptionPopup)
    return
  const s = retentionUserStore.$state
  const expired = s.vipEndTime && new Date(s.vipEndTime) < new Date()
  if (s.subscribe && s.subscribeType === VIP_TYPE.ALL && !expired) {
    retentionDebug('user became active ALL vip; dismiss retention session')
    dismissPopup()
  }
}, { immediate: true })

// 悬浮入口在离开阅读器后仍可出现；此时阅读器注册的 handler 已卸载，
// 使用 App 级实例继续为缓存的挽留档位下单。
const globalRetentionSubscription = enableRetentionSubscriptionPopup
  ? useSubscription('retention', undefined, {
    getSuccessUrl: () => window.location.href,
    markRetentionCheckout: true,
  })
  : null
const globalRetentionSubscriptionProducts = globalRetentionSubscription?.currentRetentionSubscriptionProducts

const retentionFloatingVisible = computed(() => {
  return floatingVisible.value
    && globalCountdown.value > 0
    && !floatingHiddenWhileScrolling.value
})

function handleFloatingClick() {
  showPopup(false)
}

function handleGlobalRetentionClose() {
  hidePopup()
}

let retentionActionSubmitting = false
async function handleGlobalRetentionAction(product: any) {
  // 直接对所选档位下单（复用阅读器内联充值面板的订阅下单方式），不再跳转订阅页。
  // 下单逻辑由阅读器页面通过 setRetentionActionHandler 注册；下单内部会跳转支付页。
  if (!product || retentionActionSubmitting)
    return

  retentionDebug('retention product clicked %o', {
    productId: product.productId,
    subscribeConfigId: product.subscribeConfigId,
    route: route.fullPath,
  })
  retentionActionSubmitting = true
  let submitted: boolean | undefined = false
  try {
    const readerAction = runRetentionAction(product)
    submitted = readerAction === null
      ? await (globalRetentionSubscription?.onSubscribeProducts(product) ?? false)
      : await readerAction
    retentionDebug('retention checkout submit result %o', {
      submitted,
      delegatedToReader: readerAction !== null,
      route: route.fullPath,
    })
  }
  finally {
    if (!submitted) {
      retentionDebug.warn('retention checkout was not submitted; keep popup visible')
      clearPendingPaymentCheck()
    }
    retentionActionSubmitting = false
  }
}

let pendingRetentionCancelAction: RetentionCheckoutCancelAction | null = null
function tryRestoreGlobalRetentionAfterCheckout(allowMissingPaymentStatus = false) {
  if (!enableRetentionSubscriptionPopup)
    return

  // 路由初始导航未完成时 route 是 START 占位（path='/'、无 query），
  // 此时判断支付返回路径不可靠；延迟到初始导航解析后再检查，pending 标记保留。
  if (!retentionRouteReady) {
    retentionDebug('defer checkout restore until initial navigation resolves')
    return
  }

  const cancelAction = getRetentionCheckoutCancelAction(
    route.query.payment_status,
    allowMissingPaymentStatus,
  )
  if (!cancelAction)
    return

  // BFCache 恢复阅读器时，route query 可能先于 pageshow 更新。阅读器随后会
  // window.location.reload()，因此这里必须保留 pending，交给 reload 后的新实例恢复。
  if (pageHiddenForExternalNavigation && (route.name === 'chapter' || route.name === 'chapter-scroll')) {
    retentionDebug('defer checkout restore until reader BFCache reload')
    return
  }

  pendingRetentionCancelAction = cancelAction

  // /paying 是支付结果中转页，稍后还会 replace 到 redirect_source。
  // 此处不能提前消费 pending 标记，否则最终落地页无法恢复挽留弹窗。
  if (route.path === '/paying') {
    retentionDebug('defer checkout restore on paying relay page')
    return
  }

  retentionDebug('payment return detected %o', {
    returnType: cancelAction,
    route: route.fullPath,
    hasCachedProduct: !!globalRetentionProduct.value,
    fetchedProductCount: globalRetentionSubscriptionProducts?.value?.length || 0,
    countdown: globalCountdown.value,
    popupVisible: globalPopupVisible.value,
    floatingVisible: floatingVisible.value,
  })

  // 取消动作已由 getRetentionCheckoutCancelAction 统一确认。时机 1 沿用
  // 原倒计时会话，时机 3 在 restorePopup 中首次开启会话。
  const canRestore = shouldRestoreOnCheckoutCancel()
  if (!canRestore) {
    pendingRetentionCancelAction = null
    retentionDebug('skip restoring popup after payment return')
    return
  }

  if (!globalRetentionProduct.value && globalRetentionSubscriptionProducts?.value?.length)
    globalRetentionProduct.value = globalRetentionSubscriptionProducts.value[0]

  if (!globalRetentionProduct.value) {
    retentionDebug('wait for retention product before restoring popup')
    return
  }

  retentionDebug('restore popup after payment return %o', {
    productId: globalRetentionProduct.value.productId,
    countdown: globalCountdown.value,
  })
  restorePopup()
  clearPendingPaymentCheck()
  pendingRetentionCancelAction = null
}

// 支付取消可能返回阅读器之外的任意页面（用户可通过全局悬浮入口下单），
// 因此在 App 层统一恢复；商品接口较慢时，档位加载完成后再补做一次检查。
watch(() => route.query.payment_status, (paymentStatus) => {
  if (!enableRetentionSubscriptionPopup)
    return
  if (paymentStatus === 'success') {
    clearPendingPaymentCheck()
    return
  }
  tryRestoreGlobalRetentionAfterCheckout()
}, { immediate: true })

// 阅读器与 App 会分别请求挽留档位。整页恢复时若缓存档位暂时被空列表覆盖，
// 任一请求重新补齐选中档位后都应继续完成浏览器返回恢复。
watch(globalRetentionProduct, () => {
  if (pendingRetentionCancelAction)
    tryRestoreGlobalRetentionAfterCheckout(pendingRetentionCancelAction === 'browser-back')
})

if (globalRetentionSubscriptionProducts) {
  watch(globalRetentionSubscriptionProducts, () => {
    tryRestoreGlobalRetentionAfterCheckout(pendingRetentionCancelAction === 'browser-back')
  }, { immediate: true })
}
</script>

<template>
  <div
    id="app"
    :class="[
      brandSkinClass,
      { 'ss-reader-font': brand === 'superstory' && ['chapter', 'chapter-scroll'].includes(String(route.name)) },
      theme.components?.siteFamily ? `site-family-${theme.components.siteFamily}` : '',
    ]"
  >
    <div v-if="loading">
      <template v-if="isFlipnovelFamily">
        <component :is="AppInitialLoading" />
      </template>
      <template v-else>
        <div class="pt-20vh flex justify-center">
          <BasicSpinner class="mx-auto" />
        </div>
      </template>
    </div>

    <div v-else :class="{ 'with-top-app-banner': topBannerVisible }">
      <BizTopAppBanner v-if="topBannerVisible" @close="closeTopBanner" />
      <component :is="AppGlobalHeader" v-if="isFlipnovelFamily" />
      <keep-alive>
        <router-view v-if="$route.meta?.keepAlive" />
      </keep-alive>
      <router-view v-if="!$route.meta?.keepAlive" />

      <BasicLoading />
      <BasicToast class="z-1001" />

      <component :is="BrandExperience" v-if="BrandExperience" />
      <AudioPlayerPopup />
      <AudioPlayerMini />

      <BizAgeGateModal />
    </div>

    <portal-target name="root" multiple />

    <BizRetentionFloatingButton
      v-if="enableRetentionSubscriptionPopup"
      :visible="retentionFloatingVisible"
      :countdown="globalCountdown"
      :countdown-text="globalCountdownText"
      @click="handleFloatingClick"
    />

    <BizRetentionSubscriptionPopup
      v-if="enableRetentionSubscriptionPopup"
      v-model="globalPopupVisible"
      :product="globalRetentionProduct"
      :countdown="globalCountdown"
      :countdown-text="globalCountdownText"
      :show-close-button="globalShowCloseButton"
      @action="handleGlobalRetentionAction"
      @close="handleGlobalRetentionClose"
    />
  </div>
</template>

<style>
/* PC大屏幕适配 CSS 变量 */
:root {
  --app-max-width: 750px;
  --app-content-left: 0px;
}

@media (min-width: 768px) {
  :root {
    --app-content-left: calc((100vw - var(--app-max-width)) / 2);
  }
}

html, body {
  height: 100%;
  -webkit-overflow-scrolling: auto;
  overflow: hidden;
}
body {
  font-family: -apple-system, BlinkMacSystemFont, SF UI Text, Helvetica Neue, PingFang SC, Hiragino Sans GB, Microsoft YaHei UI, Microsoft YaHei, Arial, sans-serif;
  touch-action: pan-y;
  font-size: 16px;
  max-width: var(--var-max-body-width, 750px);
  margin: 0 auto;
  background-color: v-bind(bgGlobal);
}
#app {
  height: 100%;
  margin: 0 auto;
  background: v-bind(bgGlobal);
  position: relative;
  overflow-y: scroll;
}

/** Some global transition  */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* slide-left */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.4s ease;
}
.slide-left-enter, .slide-left-leave-to {
  transform: translateX(100%);
}
/* slide-right */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.4s ease;
}
.slide-right-enter, .slide-right-leave-to {
  transform: translateX(-100%);
}

/** Some reset css */
p {
  margin: 0;
  overflow-wrap: anywhere;
}
div, input, textarea, button, select, a {
  -webkit-tap-highlight-color: transparent!important;
}
input:focus {
  outline: 0;
}
::-webkit-scrollbar {
  display: none;
}
.box-shadow {
  box-shadow: 0 2px 10px 0 rgba(220, 224, 228, 0.40);
}
</style>

<style lang="scss">
.with-top-app-banner {
  --page-viewport-height: calc(100dvh - 60px);
  > .h-100dvh { height: var(--page-viewport-height); }
  > .min-h-100dvh { min-height: var(--page-viewport-height); }
  > .top-app-banner { position: sticky; top: 0; z-index: 100; }
  .ss-login-back { top: 92px; }
}
</style>
